import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider } from './ThemeContext';
import { LanguageProvider } from './i18n';
import { NotificationProvider } from './NotificationContext';
import { AssistantProvider, useAssistant } from './hooks/useAssistant';
import { VoiceProvider } from './voice/VoiceContext';
import ClientErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import api, { cachedGet, getToken } from './api';
import FreezeWall from './components/FreezeWall';
import { UpgradeProvider } from './components/UpgradeOverlay';

/* -- Shell components kept eager -- */
/* (Layout, Sidebar, NotificationCenter etc. are imported inside Layout) */

/* -- Lazy-loaded page chunks -- */
const Dashboard = lazy(() => import('./pages/DashboardPhase2'));
const Compose = lazy(() => import('./pages/Compose'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const StreamPage = lazy(() => import('./pages/Stream'));
const LeadsPage = lazy(() => import('./pages/Leads'));
const ProvidersPage = lazy(() => import('./pages/Providers'));
// Legacy /assistant/:id/config page retired 2026-07-28: it was an orphan
// (zero inbound links) duplicating the real persona UI at settings#persona.
function ConfigRedirect() {
  const { publicId } = useParams();
  return <Navigate to={`/assistant/${publicId || 'default'}/settings#persona`} replace />;
}
const MyAssistants = lazy(() => import('./pages/MyAssistants'));
const UsagePage = lazy(() => import('./pages/Usage'));
const UsageCalculator = lazy(() => import('./pages/UsageCalculator'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Consent = lazy(() => import('./pages/Consent'));
const ForceChangePassword = lazy(() => import('./pages/ForceChangePassword'));
const Migrate = lazy(() => import('./pages/Migrate'));
const OnboardingPage = lazy(() => import('./pages/Onboarding'));
const PostsPage = lazy(() => import('./pages/Posts'));
const RepliesPage = lazy(() => import('./pages/Replies'));
const DailyScenes = lazy(() => import('./pages/DailyScenes'));
const ImageStudio = lazy(() => import('./pages/ImageStudio'));
const Conversations = lazy(() => import('./pages/Conversations'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
const DeploymentRevealPage = lazy(() => import('./pages/DeploymentReveal'));
const OAuthLaunch = lazy(() => import('./pages/OAuthLaunch'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const DeploymentPage = lazy(() => import('./pages/Deployment'));
// Admin routes removed — now served standalone on port 21573

const FREEZE_BYPASS_PATHS = ['/consent', '/force-change-password', '/migrate', '/onboarding'];

function AuthGuard({ children }) {
  const token = getToken();
  const location = useLocation();
  const [gate, setGate] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [walletReady, setWalletReady] = useState(false);

  useEffect(() => {
    if (!token) {
      window.location.replace('/');
      return;
    }
    // Legal consent + forced password gate + W28 试用冻结状态 — one cheap check per page load.
    // Fail open on network error so a transient blip cannot lock users out.
    Promise.all([
      api.get('/auth/gate-status').then(r => r.data).catch(() => ({ consent_required: false, must_change_password: false, migration_required: false })),
      cachedGet('/wallet').then(r => r.data).catch(() => null),
    ]).then(([g, w]) => {
      setGate(g);
      setWalletData(w);
      setWalletReady(true);
    }).catch(() => {
      setGate({ consent_required: false, must_change_password: false, migration_required: false });
      setWalletData(null);
      setWalletReady(true);
    });
  }, [token]);

  if (!token) return null;
  if (!gate) return null;

  if (gate.consent_required) {
    if (location.pathname !== '/consent') return <Navigate to="/consent" replace />;
  } else if (location.pathname === '/consent') {
    return <Navigate to="/" replace />;
  }

  if (!gate.consent_required && gate.must_change_password) {
    if (location.pathname !== '/force-change-password') return <Navigate to="/force-change-password" replace />;
  } else if (location.pathname === '/force-change-password') {
    return <Navigate to="/" replace />;
  }

  if (!gate.consent_required && !gate.must_change_password && gate.migration_required) {
    if (location.pathname !== '/migrate') return <Navigate to="/migrate" replace />;
  } else if (location.pathname === '/migrate') {
    return <Navigate to="/" replace />;
  }

  // W28 业务冻结门: 试用时间到 / credit 耗尽 → 全屏毛玻璃激活墙 (仅激活卡, 无只读入口)
  // consent/密码/迁移/新手引导等全屏门不受冻结墙拦截, 保证用户先完成必要流程。
  // wallet 未就绪时保持等待 (return null), 避免冻结用户的 children 渲染触发业务请求
  // 403 → api.js 拦截器重定向 → 冻结墙被导航打断。
  if (!FREEZE_BYPASS_PATHS.includes(location.pathname)) {
    if (!walletReady) return null;
    const locked = walletData?.locked_reason === 'trial_expired'
      || walletData?.locked_reason === 'trial_credit_exhausted';
    if (locked) {
      return <FreezeWall lockedReason={walletData.locked_reason} walletData={walletData} />;
    }
  }

  return children;
}

function LandingRedirect() {
  useEffect(() => {
    window.location.replace('/');
  }, []);
  return null;
}

/** LegacySettingsRedirect -- /settings → /assistant/default/settings，保留 query（Stripe 回跳参数 tab/voice_pay） */
function LegacySettingsRedirect() {
  const location = useLocation();
  return <Navigate to={`/assistant/default/settings${location.search}${location.hash}`} replace />;
}

/**
 * AssistantWorkspace -- resolves the first active assistant and redirects
 * /cortex/ -> /cortex/assistants  (list all)
 * /cortex/ (authenticated with assistants) -> /cortex/assistant/:id/dashboard
 */
function AssistantWorkspaceRedirect() {
  const navigate = useNavigate();
  const { setAssistant } = useAssistant();
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    redirected.current = true;

    const token = getToken();
    if (!token) return;

    console.log('[App] Fetching assistants after login');
      cachedGet('/assistants')
      .then(r => r.data)
      .then(res => {
        const list = res?.assistants || res?.data?.assistants || [];
        const lastUsedId = res?.data?.last_used_assistant_id ?? (res?.last_used_assistant_id ?? null);
        // 模式内解析链: 最近使用(仅 active) → 默认 → 首个 active → 列表首个 → 空态引导
        const lastUsed = list.find(a => a.id === lastUsedId && a.status === 'active');
        const defaultOne = list.find(a => a.is_default);
        const active = list.find(a => a.status === 'active');
        const chosen = lastUsed || defaultOne || active || list[0];
        if (chosen) {
          setAssistant(chosen);
          localStorage.setItem('active_assistant_id', String(chosen.id));
          const pubId = chosen.public_id || 'default';
          navigate(`/assistant/${pubId}/dashboard`, { replace: true });
        } else {
          // 零助理: 首次登录走引导页, 已完成/跳过则直接进空态列表
          return cachedGet('/auth/me')
            .then(me => {
              if (!me.data?.user?.onboarding_done) {
                navigate('/onboarding', { replace: true });
              } else {
                navigate('/assistants', { replace: true });
              }
            })
            .catch(() => navigate('/assistants', { replace: true }));
        }
      })
      .catch(() => navigate('/assistants', { replace: true }));
  }, [navigate, setAssistant]);

  return null;
}

function AssistantRouteScope() {
  const { publicId } = useParams();
  const { setAssistant } = useAssistant();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [noAssistants, setNoAssistants] = useState(false);
  const [noAssistantsTarget, setNoAssistantsTarget] = useState(null);
  const [defaultRedirect, setDefaultRedirect] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setNoAssistants(false);
    setDefaultRedirect(null);



    // Zero-assistant gate + 'default' slug resolution (single list fetch).
    // A user with no assistant has nothing to do inside an assistant
    // workspace — send them to the guided empty state instead of rendering
    // pages that can only show empty lists or fail on write.
    cachedGet('/assistants')
      .then(r => r.data)
      .then(res => {
        const list = res?.assistants || res?.data?.assistants || [];
        if (cancelled || !Array.isArray(list)) return;
        if (list.length === 0) {
          setNoAssistants(true);
          setReady(true);
          // 首次登录引导页 vs 空态列表 (已完成/跳过引导的用户)
          cachedGet('/auth/me')
            .then(me => {
              if (cancelled) return;
              setNoAssistantsTarget(me.data?.user?.onboarding_done ? '/assistants' : '/onboarding');
            })
            .catch(() => { if (!cancelled) setNoAssistantsTarget('/assistants'); });
          return;
        }
        if (publicId === 'default') {
          // Resolve the legacy 'default' slug to the real assistant so every
          // assistant-scoped page gets a valid context (dashboard, stream...).
          // Without this, pages gating on a resolved assistant id spin forever.
          const lastUsedId = res?.last_used_assistant_id ?? (res?.data?.last_used_assistant_id ?? null);
          const chosen = list.find(a => a.id === lastUsedId && a.status === 'active')
            || list.find(a => a.is_default)
            || list.find(a => a.status === 'active')
            || list[0];
          const realPub = chosen?.public_id;
          if (realPub) {
            const suffix = location.pathname.replace(/^\/assistant\/default/, '') || '/dashboard';
            setDefaultRedirect(`/assistant/${realPub}${suffix}${location.search}${location.hash}`);
          }
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled && publicId === 'default') setReady(true);
      });

    if (!publicId) {
      setReady(true);
      return () => { cancelled = true; };
    }
    if (publicId === 'default') {
      return () => { cancelled = true; };
    }

    cachedGet('/assistants/by-public/' + publicId)
      .then(r => r.data)
      .then(res => {
        if (cancelled) return;
        const assistant = res?.assistant || res;
        if (assistant?.id) {
          const previous = localStorage.getItem('active_assistant_id');
          setAssistant(assistant);
          if (previous !== String(assistant.id)) {
            window.dispatchEvent(new Event('assistant:changed'));
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setReady(true);
      });

    return () => { cancelled = true; };
  }, [publicId, setAssistant, location.pathname, location.search, location.hash]);

  if (!ready) return <LoadingFallback />;
  if (noAssistants) {
    // 等 /auth/me 决定去向, 避免闪现到错误页面
    if (!noAssistantsTarget) return <LoadingFallback />;
    return <Navigate to={noAssistantsTarget} replace />;
  }
  if (defaultRedirect) return <Navigate to={defaultRedirect} replace />;
  return <Outlet />;
}

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full min-h-[300px]">
      <div className="text-center" style={{ color: 'var(--text-muted)' }}>
        <svg className="animate-spin mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <p className="text-sm">Loading...</p>
      </div>
    </div>
  );
}

function AppContent() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
      {/* Admin routes removed — standalone on port 21573 */}
      <Route path="/login" element={<LandingRedirect />} />
      {/* OAuth callback — popup relay, no auth needed */}
      <Route path="/oauth-callback" element={<OAuthCallback />} />
      <Route path="/oauth-launch/:platform" element={<OAuthLaunch />} />
      {/* Public reveal link for deployment key backup (single-use token) */}
      <Route path="/deployment-reveal/:token" element={<DeploymentRevealPage />} />
      {/* Full-screen gates — inside AuthGuard but outside Layout (no sidebar) */}
      <Route path="/consent" element={<AuthGuard><Consent /></AuthGuard>} />
      {/* 首次登录引导 (无侧边栏全屏) — 内部自门控: 已建助理/已完成引导则跳走 */}
      <Route path="/onboarding" element={<AuthGuard><OnboardingPage /></AuthGuard>} />
      <Route path="/force-change-password" element={<AuthGuard><ForceChangePassword /></AuthGuard>} />
      <Route path="/migrate" element={<AuthGuard><Migrate /></AuthGuard>} />
      <Route path="/" element={
        <AuthGuard>
          <Layout />
        </AuthGuard>
      }>
        {/* Root redirect: go to first assistant's workspace */}
        <Route index element={<AssistantWorkspaceRedirect />} />

        {/* My Assistants list */}
        <Route path="assistants" element={<MyAssistants />} />

/* ─── Public ID route (no identifying info in URL) ─── */
        <Route path="assistant/:publicId" element={<AssistantRouteScope />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="providers" element={<ProvidersPage />} />
          <Route path="compose" element={<Compose />} />
          <Route path="stream" element={<StreamPage />} />
          <Route path="posts" element={<PostsPage />} />
          <Route path="replies" element={<RepliesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="usage" element={<UsagePage />} />
          <Route path="usage/calculator" element={<UsageCalculator />} />
          <Route path="config" element={<ConfigRedirect />} />
          <Route path="dailyscenes" element={<DailyScenes />} />
          <Route path="image-studio" element={<ImageStudio />} />
          <Route path="conversations" element={<Conversations />} />
          <Route path="knowledge" element={<KnowledgeBase />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="deployment" element={<DeploymentPage />} />
        </Route>

        {/* Legacy top-level routes (backward compat -- redirect to first assistant) */}
        <Route path="dashboard" element={<Navigate to="/assistant/default/dashboard" replace />} />
        <Route path="leads" element={<Navigate to="/assistant/default/leads" replace />} />
        <Route path="providers" element={<Navigate to="/assistant/default/providers" replace />} />
        <Route path="compose" element={<Navigate to="/assistant/default/compose" replace />} />
        <Route path="campaigns" element={<Navigate to="/assistant/default/campaigns" replace />} />
        <Route path="stream" element={<Navigate to="/assistant/default/stream" replace />} />
        // Posts removed
        // Replies removed
        <Route path="usage" element={<UsagePage />} />
        <Route path="usage/calculator" element={<UsageCalculator />} />
        <Route path="settings" element={<LegacySettingsRedirect />} />
        // DailyScenes removed
        <Route path="image-studio" element={<ImageStudio />} />
        <Route path="assistant" element={<Navigate to="/assistant/default/config" replace />} />
        <Route path="conversations" element={<Navigate to="/assistant/default/conversations" replace />} />

        {/* System pages (not assistant-scoped) */}
        <Route path="change-password" element={<ChangePassword />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <NotificationProvider>
          <UpgradeProvider>
            <AssistantProvider>
              <VoiceProvider>
                <ClientErrorBoundary>
                  <AppContent />
                </ClientErrorBoundary>
              </VoiceProvider>
            </AssistantProvider>
          </UpgradeProvider>
        </NotificationProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}
